import React, { useCallback, useMemo } from "react";
import { useImmer } from "use-immer";

import { IgButton, IgPaper, IgTypography } from "@intelligems/ig-theme";
import {
  MessageBoxVariant,
  MessageBoxWidgetDto,
  ProgressBarWidgetDto,
  ProgressBarWidgetVariant,
  QuantityButtonsVariant,
  QuantityButtonsWidgetDto,
  WidgetRenderLocation,
} from "@intelligems/ig-types";
import { Drawer, Grid, useTheme } from "@mui/material";
import {
  generateMessageBoxWidgetForPreview,
  generateProgressBarWidgetForPreview,
  generateQuantityButtonWidgetForPreview,
} from "@src/components/ColorAndStyle/utils/utils";
import { DeviceWidget } from "@src/domains/WidgetLibrary/DeviceWidget";
import { IgMessageBox } from "@src/domains/WidgetLibrary/MessageBox/IgMessageBox";
import { ProgressBarPreview } from "@src/domains/WidgetLibrary/ProgressBar/ProgressBarPreview";
import { IgQuantityButtons } from "@src/domains/WidgetLibrary/QuantityButtons/IgQuantityButtons";
import { offerExample } from "@src/domains/WidgetLibrary/stubs";
import {
  StyledBaseBox,
  StyledCompactBox,
} from "@src/features/offers/components/OffersTableCellRenderers";
import { useDispatch, useSelector } from "@src/store";
import { closeDrawer, isDrawerOpen } from "@src/store/slices/drawers";
import { updateUserInterface } from "@src/store/slices/userInterfaces";
import { selectedWidgets } from "@src/store/slices/widgets";
import { isMessageBox, isProgressBar } from "@src/store/slices/widgets/util";

interface ComponentStyleVariantSelectionDrawerProps {
  variantsList: {
    label: string;
    description: string;
    value:
      | MessageBoxVariant
      | QuantityButtonsVariant
      | ProgressBarWidgetVariant;
  }[];
  interfaceId: string;
  widget: ProgressBarWidgetDto | QuantityButtonsWidgetDto | MessageBoxWidgetDto;
}

export const ComponentStyleVariantSelectionDrawer = ({
  variantsList,
  widget,
  interfaceId,
}: ComponentStyleVariantSelectionDrawerProps) => {
  const isSelectDrawerOpen = useSelector(
    isDrawerOpen("componentStyleVariantSelection")
  );
  const theme = useTheme();
  const widgets = useSelector(selectedWidgets);
  const [selectedVariant, setSelectedVariant] = useImmer(
    widget.config.desktop?.variant ?? variantsList[0].value
  );

  const getWidget = useCallback(
    (
      style:
        | MessageBoxVariant
        | ProgressBarWidgetVariant
        | QuantityButtonsVariant
    ) => {
      if (isMessageBox(widget)) {
        return widgets.find(
          (wid) =>
            wid.config?.desktop?.variant === style &&
            (wid.widgetType === "messageBoxPopup" ||
              wid.widgetType === "messageBoxSlideOut")
        );
      } else {
        return widgets.find(
          (wid) =>
            wid.config?.desktop?.variant === style &&
            wid.widgetType === widget.widgetType
        );
      }
    },
    [selectedVariant, widget]
  );

  const variantsListWrapper = useMemo(() => {
    return variantsList.map((variant) => ({
      ...variant,
      component: isProgressBar(widget) ? (
        <ProgressBarPreview
          widget={generateProgressBarWidgetForPreview(
            getWidget(variant.value) as ProgressBarWidgetDto,
            true,
            true
          )}
          widgetOverride={generateProgressBarWidgetForPreview(
            getWidget(variant.value) as ProgressBarWidgetDto,
            true,
            true
          )}
          mode={"input"}
          isEdit={true}
          hideLabel={true}
          renderLocation={"drawer" as WidgetRenderLocation}
        />
      ) : isMessageBox(widget) ? (
        <DeviceWidget
          device={"desktop"}
          renderLocation={"drawer" as WidgetRenderLocation}
        >
          <IgMessageBox
            device={"desktop"}
            widget={generateMessageBoxWidgetForPreview(
              getWidget(variant.value) as MessageBoxWidgetDto,
              true,
              true
            )}
          />
        </DeviceWidget>
      ) : (
        <DeviceWidget
          device={"desktop"}
          renderLocation={"drawer" as WidgetRenderLocation}
        >
          <IgQuantityButtons
            widget={generateQuantityButtonWidgetForPreview(
              getWidget(variant.value) as QuantityButtonsWidgetDto,
              true
            )}
            offer={offerExample}
            device={"desktop"}
          />
        </DeviceWidget>
      ),
    }));
  }, [variantsList, getWidget, widget]);
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer("componentStyleVariantSelection"));
  };
  const onDoneClick = () => {
    const widget = getWidget(selectedVariant);
    if (widget) {
      dispatch(updateUserInterface({ id: interfaceId, widget }));
    }
    handleClose();
  };

  return (
    <Drawer
      sx={{
        zIndex: 50000,
        ".MuiDrawer-paper": {
          borderRadius: "0 !important",
          padding: "0",
          display: "flex",
          flexDirection: "column",
          // overflow: "hidden",
        },
      }}
      onClose={handleClose}
      open={isSelectDrawerOpen}
      anchor={"right"}
    >
      <Grid container direction={"column"}>
        <Grid
          item
          container
          direction={"column"}
          rowSpacing={5}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            padding: "20px",
            paddingBottom: "0",
          }}
        >
          {variantsListWrapper.map((variant, index) => (
            <Grid
              item
              container
              direction={"column"}
              key={index}
              rowSpacing={3}
              onClick={() => {
                setSelectedVariant(variant.value);
              }}
            >
              <Grid item>
                <IgPaper
                  isClickable={true}
                  isSelected={selectedVariant === variant.value}
                  sx={{
                    width: "100%",
                    padding: 0,
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ...(selectedVariant === variant.value && {
                      backgroundColor: theme.igBackgroundColors.accent,
                      border: `1px solid ${theme.igBorders.accent.active}`,
                      "&:hover": {
                        backgroundColor: theme.igBackgroundColors.accent,
                        border: `1px solid ${theme.igBorders.accent.active}`,
                        cursor: "pointer",
                      },
                    }),
                    ...(selectedVariant !== variant.value && {
                      "&:hover": {
                        border: `1px solid ${theme.igBorders.core.hover}`,
                        cursor: "pointer",
                      },
                      border: "1px solid transparent",
                      backgroundColor:
                        theme.igBackgroundColors.interactive.neutral.default,
                    }),
                  }}
                >
                  {variant.value === "quantityButtonsWidgetCompact" ? (
                    <StyledCompactBox sx={{ minWidth: "210px" }}>
                      {variant.component}
                    </StyledCompactBox>
                  ) : (
                    <StyledBaseBox
                      sx={{
                        minWidth: "210px",
                        [`& .ig-quantity-image-placeholder`]: {
                          width: "25px !important",
                          height: "25px !important",
                          maxWidth: "20px !important",
                          minWidth: "20px !important",
                          minHeight: "20px !important",
                          maxHeight: "20px !important",
                        },
                      }}
                    >
                      {variant.component}
                    </StyledBaseBox>
                  )}
                </IgPaper>
              </Grid>
              <Grid item container direction={"column"}>
                <Grid item>
                  <IgTypography>{variant.label}</IgTypography>
                </Grid>
                <Grid item>
                  <IgTypography
                    variant={"body2"}
                    color={theme.palette.text.secondary}
                  >
                    {variant.description}
                  </IgTypography>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid
          textAlign={"center"}
          item
          width={"100%"}
          sx={{
            backgroundColor: "white",
            padding: "16px",
          }}
        >
          <IgButton onClick={onDoneClick} fullWidth>
            Done
          </IgButton>
        </Grid>
      </Grid>
    </Drawer>
  );
};
